import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Camera } from "@capacitor/camera";
import { CameraResultType, CameraSource } from "@capacitor/camera";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { HairstyleCarousel } from "@/components/HairstyleCarousel";
import { FacialHairCarousel } from "@/components/FacialHairCarousel";
import { ImageCropper } from "@/components/ImageCropper";
import { ImageComparisonSlider } from "@/components/ui/image-comparison-slider-horizontal";
import { ShareButtons } from "@/components/ShareButtons";
import { compressImageBlob } from "@/lib/imageUtils";
import { revokeTrackedBlobUrl, forceGarbageCollection, aggressiveCleanup } from "@/lib/memoryUtils";
import { saveGeneration } from "@/lib/generationHistory";
import { getSliderImageUrl, getPreviewImageUrl } from "@/lib/imageOptimization";
import { logMemoryStats } from "@/lib/memoryMonitor";
import { UpgradeModal } from "@/components/UpgradeModal";
import { LumaSpin } from "@/components/ui/luma-spin";
import { Camera as CameraIcon, Upload, History, Settings, LogOut, Crown, Scissors } from "lucide-react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useSubscription } from "@/hooks/useSubscription";
import cutPreviewsLogo from "@/assets/cut-previews-logo.png";
import { compressImage, base64ToBlob } from "@/lib/imageUtils";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

interface Hairstyle {
  id: string;
  name: string;
  image_url: string;
  category: string | null;
}

interface FacialHair {
  id: string;
  name: string;
  image_url: string;
  category: string | null;
}

type StyleType = 'hairstyle' | 'facial_hair';

const Index = () => {
  const { user, isAdmin, signOut, loading: authLoading } = useAuth();
  const { generationCount, checkSubscription } = useSubscription();
  const navigate = useNavigate();
  const [userPhoto, setUserPhoto] = useState<string | null>(null);
  const [tempPhoto, setTempPhoto] = useState<string | null>(null);
  const [showCropper, setShowCropper] = useState(false);
  const [selectedHairstyle, setSelectedHairstyle] = useState<Hairstyle | null>(null);
  const [selectedFacialHair, setSelectedFacialHair] = useState<FacialHair | null>(null);
  const [activeStyleType, setActiveStyleType] = useState<StyleType>('hairstyle');
  const [resultPhoto, setResultPhoto] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [userEthnicity, setUserEthnicity] = useState<string | null>(null);
  const [showEthnicityPrompt, setShowEthnicityPrompt] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const { toast } = useToast();
  
  // Log memory stats on mount (debug)
  useEffect(() => {
    logMemoryStats();
  }, []);

  useEffect(() => {
    if (!authLoading && !user) {
      navigate("/auth");
    } else if (user) {
      // Defer profile fetch to prevent auth deadlock
      setTimeout(() => {
        fetchUserProfile();
      }, 0);
    }
  }, [user, authLoading, navigate]);

  // Enhanced cleanup when photos change with tracked URLs
  useEffect(() => {
    return () => {
      if (tempPhoto && tempPhoto.startsWith('blob:')) {
        revokeTrackedBlobUrl(tempPhoto);
      }
    };
  }, [tempPhoto]);

  useEffect(() => {
    return () => {
      if (userPhoto && userPhoto.startsWith('blob:')) {
        revokeTrackedBlobUrl(userPhoto);
      }
    };
  }, [userPhoto]);

  useEffect(() => {
    return () => {
      if (resultPhoto && resultPhoto.startsWith('blob:')) {
        revokeTrackedBlobUrl(resultPhoto);
      }
    };
  }, [resultPhoto]);

  const fetchUserProfile = async () => {
    if (!user) return;

    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('ethnicity')
        .eq('id', user.id)
        .maybeSingle();

      if (error) throw error;

      if (data?.ethnicity) {
        setUserEthnicity(data.ethnicity);
      } else {
        // Prompt user to set their ethnicity if not set
        setShowEthnicityPrompt(true);
      }
    } catch (error) {
      console.error('Error fetching profile:', error);
      setShowEthnicityPrompt(true);
    }
  };

  const updateUserEthnicity = async (ethnicity: string) => {
    if (!user) return;

    try {
      const { error } = await supabase
        .from('profiles')
        .update({ ethnicity })
        .eq('id', user.id);

      if (error) throw error;

      setUserEthnicity(ethnicity);
      setShowEthnicityPrompt(false);
      
      toast({
        title: "Profile Updated",
        description: "Hairstyles now filtered by your preference!",
      });
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  const takePicture = async (source: CameraSource) => {
    try {
      // AGGRESSIVE memory cleanup before capture
      await aggressiveCleanup();
      
      // Clear all state
      if (tempPhoto) revokeTrackedBlobUrl(tempPhoto);
      if (userPhoto && userPhoto.startsWith('blob:')) revokeTrackedBlobUrl(userPhoto);
      if (resultPhoto && resultPhoto.startsWith('blob:')) revokeTrackedBlobUrl(resultPhoto);
      
      setTempPhoto(null);
      setUserPhoto(null);
      setResultPhoto(null);
      setSelectedHairstyle(null);
      setSelectedFacialHair(null);
      
      // Extra GC pause for mobile
      await forceGarbageCollection(300);
      
      const image = await Camera.getPhoto({
        quality: 70, // Reduced quality to prevent white flash
        allowEditing: false,
        resultType: CameraResultType.Uri,
        source,
        width: 800, // Reduced size for mobile memory
        height: 800
      });

      if (image.webPath) {
        // Load blob from camera
        const response = await fetch(image.webPath);
        const originalBlob = await response.blob();
        
        // CRITICAL: Revoke the webPath if it's a blob URL
        if (image.webPath.startsWith('blob:')) {
          URL.revokeObjectURL(image.webPath);
        }
        
        // IMMEDIATELY compress before showing cropper
        const compressedBlob = await compressImageBlob(originalBlob);
        
        // Create object URL only for the compressed version
        const objectURL = URL.createObjectURL(compressedBlob);
        setTempPhoto(objectURL);
        
        // Small delay to prevent white flash
        await forceGarbageCollection(100);
        setShowCropper(true);
      }
    } catch (error) {
      console.error('Camera error:', error);
      toast({
        title: "Camera error",
        description: "Failed to capture photo. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleCropComplete = async (croppedImageURL: string) => {
    // Clear temp immediately to free memory
    if (tempPhoto) revokeTrackedBlobUrl(tempPhoto);
    setTempPhoto(null);
    setShowCropper(false);
    
    if (!user) return;
    
    try {
      // Fetch the cropped blob - already compressed from camera
      const response = await fetch(croppedImageURL);
      const croppedBlob = await response.blob();
      
      // Revoke the cropped image URL immediately
      URL.revokeObjectURL(croppedImageURL);
      
      const timestamp = Date.now();
      const path = `${user.id}/temp_${timestamp}.jpg`;
      
      const { error } = await supabase.storage
        .from('user-photos')
        .upload(path, croppedBlob, { contentType: 'image/jpeg' });
      
      if (error) throw error;
      
      // Store only the URL in state, not blob
      const { data: { publicUrl } } = supabase.storage
        .from('user-photos')
        .getPublicUrl(path);
      
      setUserPhoto(publicUrl);
      
      // Force garbage collection hint with aggressive cleanup
      await forceGarbageCollection(100);
    } catch (error) {
      console.error('Upload error:', error);
      toast({
        title: "Upload failed",
        description: "Please try again",
        variant: "destructive",
      });
    }
  };

  const handleCropCancel = () => {
    setShowCropper(false);
    if (tempPhoto) revokeTrackedBlobUrl(tempPhoto);
    setTempPhoto(null);
  };

  const applyStyle = async () => {
    const isHairstyle = activeStyleType === 'hairstyle';
    const selectedStyle = isHairstyle ? selectedHairstyle : selectedFacialHair;
    
    if (!userPhoto || !selectedStyle) {
      toast({
        title: "Missing information",
        description: `Please capture a photo and select a ${isHairstyle ? 'hairstyle' : 'facial hair style'}`,
        variant: "destructive",
      });
      return;
    }

    // AGGRESSIVE cleanup before generating
    if (resultPhoto && resultPhoto.startsWith('blob:')) {
      revokeTrackedBlobUrl(resultPhoto);
    }
    setResultPhoto(null);
    
    // Wait for cleanup with extra GC hint
    await forceGarbageCollection(150);
    
    setLoading(true);
    
    try {
      console.log('UserPhoto URL:', userPhoto.substring(0, 50));
      
      const { data, error } = await supabase.functions.invoke('apply-hairstyle', {
        body: {
          userPhotoUrl: userPhoto,
          hairstyleDescription: selectedStyle.name,
          styleType: activeStyleType
        }
      });

      console.log('=== Apply hairstyle response ===');
      console.log('Data:', data);
      console.log('Error:', error);

      // Check for limitReached in data first (status 200 response)
      if (data?.limitReached === true) {
        console.log('✓ Limit reached - showing upgrade modal');
        setShowUpgradeModal(true);
        setLoading(false);
        return;
      }

      // Check for errors
      if (error) {
        console.error('Edge function error:', error);
        throw error;
      }

      if (data?.imageUrl) {
        // Edge function already uploaded to storage, just use the URL
        setResultPhoto(data.imageUrl);
        
        // Refresh subscription/generation count
        checkSubscription();
        
        // Save to IndexedDB in background (non-blocking)
        if (user) {
          const generationRecord = {
            id: `gen_${Date.now()}`,
            userId: user.id,
            originalPhotoUrl: userPhoto,
            resultPhotoUrl: data.imageUrl,
            hairstyleId: activeStyleType === 'hairstyle' ? selectedHairstyle?.id : undefined,
            hairstyleName: activeStyleType === 'hairstyle' ? selectedHairstyle?.name : undefined,
            facialHairId: activeStyleType === 'facial_hair' ? selectedFacialHair?.id : undefined,
            facialHairName: activeStyleType === 'facial_hair' ? selectedFacialHair?.name : undefined,
            createdAt: Date.now()
          };
          
          saveGeneration(generationRecord).catch(e => {
            console.error('Failed to save to IndexedDB:', e);
          });
          
          // Also save to Supabase in background (for sync across devices)
          (async () => {
            try {
              const insertData: any = {
                user_id: user.id,
                original_photo_url: userPhoto,
                result_photo_url: data.imageUrl
              };
              
              if (activeStyleType === 'hairstyle' && selectedHairstyle) {
                insertData.hairstyle_id = selectedHairstyle.id;
                insertData.hairstyle_name = selectedHairstyle.name;
              } else if (activeStyleType === 'facial_hair' && selectedFacialHair) {
                insertData.facial_hair_id = selectedFacialHair.id;
                insertData.facial_hair_name = selectedFacialHair.name;
              }
              
              await supabase.from('generations').insert(insertData);
            } catch (error) {
              console.error('DB save error:', error);
            }
          })();
        }
        
        // Log memory after generation
        logMemoryStats();
      }
    } catch (error: any) {
      console.error('Error applying hairstyle:', error);
      
      // Check if it's a limit reached error
      const errorContext = error?.context || {};
      if (errorContext.limitReached || error.message?.includes('free generations')) {
        setShowUpgradeModal(true);
        return;
      }
      
      let errorMessage = "Failed to apply hairstyle. Please try again.";
      if (error.message?.includes('timeout') || error.message?.includes('timed out')) {
        errorMessage = "Request timed out. Please try with a smaller image or try again.";
      } else if (error.message?.includes('Rate limit')) {
        errorMessage = "Too many requests. Please wait a moment and try again.";
      } else if (error.message?.includes('Credits')) {
        errorMessage = "AI credits exhausted. Please add credits to your workspace.";
      }
      
      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {showCropper && tempPhoto && (
        <ImageCropper
          image={tempPhoto}
          onCropComplete={handleCropComplete}
          onCancel={handleCropCancel}
        />
      )}
      <div className="flex flex-col h-screen bg-gradient-to-br from-background to-secondary">
      {/* Header - Compact */}
      <header className="py-2 px-4 bg-card/80 backdrop-blur-sm border-b">
        <div className="flex items-center justify-between max-w-md mx-auto">
          <div className="flex items-center gap-2">
            {!generationCount.loading && (
              <div className="text-[10px] bg-muted px-1.5 py-0.5 rounded-full">
                {isAdmin ? (
                  <>∞</>
                ) : (
                  <>{generationCount.used}/{generationCount.limit}</>
                )}
              </div>
            )}
          </div>
          <img 
            src={cutPreviewsLogo} 
            alt="Cut Previews" 
            className="h-8 w-auto"
            width="102"
            height="64"
          />
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="h-8 w-8">
                <Settings className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => navigate("/movember")}>
                <Scissors className="mr-2 h-4 w-4" />
                Movember
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate("/subscription")}>
                <Crown className="mr-2 h-4 w-4" />
                Subscription
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setShowEthnicityPrompt(true)}>
                <Settings className="mr-2 h-4 w-4" />
                Change Ethnicity Filter
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate("/history")}>
                <History className="mr-2 h-4 w-4" />
                My Generations
              </DropdownMenuItem>
              {isAdmin && (
                <DropdownMenuItem onClick={() => navigate("/admin")}>
                  <Settings className="mr-2 h-4 w-4" />
                  Admin Panel
                </DropdownMenuItem>
              )}
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={signOut}>
                <LogOut className="mr-2 h-4 w-4" />
                Sign Out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </header>

      {/* Main Content - Percentage based */}
      <main className="flex-1 flex flex-col overflow-hidden">
        <div className="flex-1 flex flex-col max-w-md mx-auto w-full p-4 gap-3">
          {/* Photo Display - 45% of viewport */}
          <Card className="overflow-hidden relative" style={{ height: '45vh' }}>
            <div className="w-full h-full bg-muted flex items-center justify-center">
              {resultPhoto && userPhoto ? (
                <ImageComparisonSlider 
                  leftImage={getSliderImageUrl(userPhoto)} 
                  rightImage={getSliderImageUrl(resultPhoto)} 
                  altLeft="Before" 
                  altRight="After" 
                />
              ) : userPhoto ? (
                <img
                  src={getPreviewImageUrl(userPhoto)}
                  alt="User"
                  className="w-full h-full object-contain"
                />
              ) : (
                <div className="text-center text-muted-foreground p-4">
                  <CameraIcon className="h-12 w-12 mx-auto mb-2 opacity-50" />
                  <p className="text-sm">Take or upload a photo</p>
                </div>
              )}
            </div>
            {loading && (
              <div className="absolute inset-0 bg-black/70 flex items-center justify-center z-10">
                <LumaSpin />
              </div>
            )}
          </Card>

          {/* All Buttons in One Row */}
          <div className="flex gap-3">
            {/* Camera/Gallery Buttons - 2/3 width */}
            <div className="grid grid-cols-2 gap-3 flex-[2]">
              <Button
                onClick={() => takePicture(CameraSource.Camera)}
                size="sm"
                className="w-full h-10"
              >
                <CameraIcon className="mr-2 h-4 w-4" />
                Camera
              </Button>
              <Button
                onClick={() => takePicture(CameraSource.Photos)}
                variant="secondary"
                size="sm"
                className="w-full h-10"
              >
                <Upload className="mr-2 h-4 w-4" />
                Gallery
              </Button>
            </div>

            {/* Share/Download Buttons - 1/3 width */}
            {resultPhoto && (selectedHairstyle || selectedFacialHair) && (
              <div className="flex-1 flex items-center justify-end gap-2">
                <ShareButtons 
                  imageUrl={resultPhoto} 
                  hairstyleName={selectedHairstyle?.name || selectedFacialHair?.name || ''}
                  iconOnly={true}
                />
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="text-center text-[10px] text-muted-foreground">
            <a href="/privacy" className="hover:text-primary underline">Privacy Policy</a>
          </div>
        </div>
      </main>

      {/* Style Selection with Tabs - Fixed height */}
      <div className="bg-card/80 backdrop-blur-sm border-t" style={{ height: '30vh' }}>
        <Tabs value={activeStyleType} onValueChange={(v) => setActiveStyleType(v as StyleType)} className="w-full h-full flex flex-col">
          <div className="border-b bg-muted/30">
            <TabsList className="w-full h-10 rounded-none bg-transparent p-0">
              <TabsTrigger 
                value="hairstyle" 
                className="flex-1 h-full rounded-none data-[state=active]:bg-background data-[state=active]:shadow-sm text-xs"
              >
                <Scissors className="mr-1 h-3 w-3" />
                Hairstyles
              </TabsTrigger>
              <TabsTrigger 
                value="facial_hair" 
                className="flex-1 h-full rounded-none data-[state=active]:bg-background data-[state=active]:shadow-sm text-xs"
              >
                <Crown className="mr-1 h-3 w-3" />
                Facial Hair
              </TabsTrigger>
            </TabsList>
          </div>
          <TabsContent value="hairstyle" className="mt-0 flex-1 overflow-hidden">
            {activeStyleType === 'hairstyle' && (
              <HairstyleCarousel
                onSelect={(style) => {
                  setSelectedHairstyle(style);
                  setSelectedFacialHair(null);
                }}
                onApply={applyStyle}
                selectedId={selectedHairstyle?.id}
                userEthnicity={userEthnicity}
                hasUserPhoto={!!userPhoto}
              />
            )}
          </TabsContent>
          <TabsContent value="facial_hair" className="mt-0 flex-1 overflow-hidden">
            {activeStyleType === 'facial_hair' && (
              <FacialHairCarousel
                onSelect={(style) => {
                  setSelectedFacialHair(style);
                  setSelectedHairstyle(null);
                }}
                onApply={applyStyle}
                selectedId={selectedFacialHair?.id}
                userEthnicity={userEthnicity}
                hasUserPhoto={!!userPhoto}
              />
            )}
          </TabsContent>
        </Tabs>
      </div>

      {/* Ethnicity Selection Dialog */}
      {showEthnicityPrompt && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="max-w-md w-full p-6 space-y-4">
            <div>
              <h3 className="text-lg font-semibold mb-2">Select Your Ethnicity</h3>
              <p className="text-sm text-muted-foreground">
                This helps us recommend hairstyles that match your features
              </p>
            </div>
            <div className="grid grid-cols-1 gap-2">
              {[
                { value: 'caucasian', label: 'Caucasian' },
                { value: 'african', label: 'African' },
                { value: 'asian', label: 'Asian' },
                { value: 'hispanic', label: 'Hispanic' },
                { value: 'middle_eastern', label: 'Middle Eastern' },
                { value: 'south_asian', label: 'South Asian' }
              ].map((option) => (
                <Button
                  key={option.value}
                  variant={userEthnicity === option.value ? "default" : "outline"}
                  onClick={() => updateUserEthnicity(option.value)}
                  className="justify-start"
                >
                  {option.label}
                </Button>
              ))}
            </div>
            {userEthnicity && (
              <Button
                variant="ghost"
                onClick={() => setShowEthnicityPrompt(false)}
                className="w-full"
              >
                Cancel
              </Button>
            )}
          </Card>
        </div>
      )}

      {/* Upgrade Modal */}
      <UpgradeModal 
        open={showUpgradeModal} 
        onOpenChange={setShowUpgradeModal}
      />
    </div>
    </>
  );
};

export default Index;
